import type { User } from 'next-auth';
import { create, type StateCreator } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { CallContractTx } from '../message-channel/message-handlers/onCallContract';
import { getLocalStorage } from '../utils/localstorage';

type SignMessageRequest = {
  type: 'signMessage';
  message: string;
  chainId: number;
};

type CallContractRequest = {
  type: 'callContract';
} & CallContractTx;

type SendRawTransactionRequest = {
  type: 'sendRawTransaction';
  transactionData: string;
  chainId: number;
  to: string;
};

type ConnectedDomain = {
  domain: string;
  walletAddress: string;
  expiresAt: number;
};

type State = {
  request:
    | SignMessageRequest
    | CallContractRequest
    | SendRawTransactionRequest
    | null;
  user: User | null;
  connectedDomains: ConnectedDomain[];
};

type Actions = {
  setRequest: (
    request:
      | SignMessageRequest
      | CallContractRequest
      | SendRawTransactionRequest
      | null
  ) => void;
  setUser: (user: User | null) => void;
  addConnectedDomain: (domain: string, walletAddress: string) => void;
  removeConnectedDomain: (domain: string) => void;
  getConnectedDomain: (
    domain: string,
    walletAddress: string
  ) => ConnectedDomain | undefined;
};

type WalletStore = State & Actions;

const walletStore: StateCreator<WalletStore> = (set, get) => ({
  request: null,
  user: null,
  connectedDomains: JSON.parse(
    getLocalStorage()?.getItem('connectedDomains') || '[]'
  ),
  setRequest: (request) => set({ request }),
  setUser: (user) => set({ user }),
  addConnectedDomain: (domain, walletAddress) => {
    const connectedDomains = get().connectedDomains;
    connectedDomains.push({
      domain,
      walletAddress,
      expiresAt: Date.now() + 1000 * 60 * 60 * 24 * 3, // 3 days
    });
    getLocalStorage()?.setItem(
      'connectedDomains',
      JSON.stringify(connectedDomains)
    );
    set({ connectedDomains });
  },
  removeConnectedDomain: (domain) => {
    const connectedDomains = get().connectedDomains.filter(
      (d) => d.domain !== domain
    );
    getLocalStorage()?.setItem(
      'connectedDomains',
      JSON.stringify(connectedDomains)
    );
    set({ connectedDomains });
  },
  getConnectedDomain: (domain, walletAddress) => {
    const connectedDomain = get().connectedDomains.find(
      (d) =>
        d.domain === domain &&
        d.walletAddress === walletAddress &&
        d.expiresAt > Date.now()
    );
    return connectedDomain;
  },
});

export default create<WalletStore>()(
  devtools(walletStore, {
    name: 'Z Wallet: Wallet Store',
  })
);

if (typeof window !== 'undefined') {
  const domains = JSON.parse(
    getLocalStorage()?.getItem('connectedDomains') || '[]'
  );
  getLocalStorage()?.setItem(
    'connectedDomains',
    JSON.stringify(
      domains.filter((d: ConnectedDomain) => d.expiresAt > Date.now())
    )
  );
}
